import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  MenuItem,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import {
  ExpandMore as ExpandMoreIcon,
  Event as EventIcon,
} from '@mui/icons-material';

const Tours = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canEditMetricsExclusion = hasRole(['admin', 'superadmin']);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [expandedTours, setExpandedTours] = useState({});
  const [expandedBookings, setExpandedBookings] = useState({});
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeDialogTour, setExcludeDialogTour] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excludeSaving, setExcludeSaving] = useState(false);

  useEffect(() => {
    loadTours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const loadTours = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        year: year,
        month: month,
      };
      const response = await axios.get('/crm/tours', { params });
      setTours(response.data.tours || []);
    } catch (err) {
      console.error('Error loading tours:', err);
      setError(err.response?.data?.detail || 'Failed to load tours');
      setTours([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTourExpand = (tourId) => (event, isExpanded) => {
    setExpandedTours((prev) => ({
      ...prev,
      [tourId]: isExpanded,
    }));
  };

  const handleBookingExpand = (bookingId) => (event, isExpanded) => {
    setExpandedBookings((prev) => ({
      ...prev,
      [bookingId]: isExpanded,
    }));
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(value);
  };

  const handleViewGuest = (guestId, event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/guests/${guestId}`);
  };

  const openExcludeDialog = (tour) => {
    setExcludeDialogTour(tour);
    setExcludeReason(tour.excluded_reason || '');
    setExcludeDialogOpen(true);
  };

  const handleConfirmExclude = async () => {
    if (!excludeDialogTour) return;
    try {
      setExcludeSaving(true);
      setError(null);
      await axios.patch(
        `/crm/tours/${excludeDialogTour.db_tour_id}/metrics-exclusion`,
        {
          excluded_from_metrics: true,
          excluded_reason: excludeReason.trim() || null,
        }
      );
      setExcludeDialogOpen(false);
      setExcludeDialogTour(null);
      await loadTours();
    } catch (err) {
      console.error('Exclude tour failed:', err);
      setError(err.response?.data?.detail || 'Failed to update tour exclusion');
    } finally {
      setExcludeSaving(false);
    }
  };

  const handleRestoreMetrics = async (tour) => {
    if (
      !window.confirm(
        'Include this tour in dashboard and report totals again? Pending review SMS are not recreated automatically. Refresh Reports for the month if you use cached historical data.'
      )
    ) {
      return;
    }
    try {
      setError(null);
      await axios.patch(`/crm/tours/${tour.db_tour_id}/metrics-exclusion`, {
        excluded_from_metrics: false,
      });
      await loadTours();
    } catch (err) {
      console.error('Restore tour metrics failed:', err);
      setError(err.response?.data?.detail || 'Failed to restore tour');
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate years (current year ± 2 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <Layout>
      <Container maxWidth={false} sx={{ width: '100%', mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <EventIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight={700}>
              Tours
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              select
              label="Year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              sx={{ minWidth: 120 }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Month"
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {tours.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No tours found for {months[month - 1].label} {year}
                </Typography>
              </Paper>
            ) : (
              <Box>
                {tours.map((tour) => (
                  <Accordion
                    key={tour.db_tour_id}
                    expanded={expandedTours[tour.db_tour_id] || false}
                    onChange={handleTourExpand(tour.db_tour_id)}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <Box flex={1}>
                          <Typography variant="h6" component="div">
                            {tour.tour_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(tour.tour_start_time, 'datetime')}
                            {tour.tour_end_time && ` - ${formatDate(tour.tour_end_time, 'time')}`}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                          {tour.excluded_from_metrics && (
                            <Chip
                              label="Excluded from metrics & review SMS"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            label={
                              tour.tour_id && tour.tour_id.length > 20
                                ? `Tour ID: ${tour.tour_id.substring(0, 20)}…`
                                : `Tour ID: ${tour.tour_id || '—'}`
                            }
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${tour.total_bookings} booking${tour.total_bookings !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                          />
                          <Chip
                            label={`${tour.total_participants} participant${tour.total_participants !== 1 ? 's' : ''}`}
                            size="small"
                            color="secondary"
                          />
                          {tour.total_revenue_gross && (
                            <Chip
                              label={formatCurrency(tour.total_revenue_gross)}
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {canEditMetricsExclusion && (
                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 2,
                              alignItems: 'center',
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={!!tour.excluded_from_metrics}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      openExcludeDialog(tour);
                                    } else {
                                      handleRestoreMetrics(tour);
                                    }
                                  }}
                                />
                              }
                              label="Exclude from dashboard, reports & pending review SMS"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 480 }}>
                              Use when a tour was cancelled after manifest: removes it from KPIs and cancels queued
                              review texts (already sent SMS cannot be recalled). Run Reports → Refresh for the month
                              to update cached totals.
                            </Typography>
                            {tour.excluded_reason && (
                              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                                Note: {tour.excluded_reason}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {tour.bookings.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No bookings found for this tour
                          </Typography>
                        ) : (
                          tour.bookings.map((booking) => (
                            <Accordion
                              key={booking.booking_id}
                              expanded={expandedBookings[booking.booking_id] || false}
                              onChange={handleBookingExpand(booking.booking_id)}
                              sx={{ mb: 1, '&:before': { display: 'none' } }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center" gap={2} width="100%">
                                  <Box flex={1}>
                                    <Typography variant="subtitle1" component="div">
                                      Booking #{booking.booking_number}
                                    </Typography>
                                    {booking.status && (
                                      <Chip
                                        label={booking.status}
                                        size="small"
                                        sx={{ mt: 0.5 }}
                                      />
                                    )}
                                  </Box>
                                  <Box display="flex" gap={2} alignItems="center">
                                    <Chip
                                      label={booking.revenue_gross ? formatCurrency(booking.revenue_gross) : 'No revenue'}
                                      size="small"
                                      variant="outlined"
                                      color={booking.revenue_gross ? "default" : "warning"}
                                    />
                                    <Chip
                                        label={`${booking.participants.filter(p => p.role === 'participant').length} participant${booking.participants.filter(p => p.role === 'participant').length !== 1 ? 's' : ''}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                  </Box>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box>
                                  {booking.participants.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                      No participants found for this booking
                                    </Typography>
                                  ) : (
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {booking.participants.map((participant) => (
                                            <TableRow key={`${booking.booking_id}-${participant.guest_id}-${participant.role}`}>
                                              <TableCell>
                                                <Typography variant="body2">
                                                  {participant.guest_name}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={participant.role.charAt(0).toUpperCase() + participant.role.slice(1)}
                                                  size="small"
                                                  color={participant.role === 'customer' ? 'primary' : 'default'}
                                                />
                                              </TableCell>
                                              <TableCell align="right">
                                                <Link
                                                  component="button"
                                                  variant="body2"
                                                  onClick={(e) => handleViewGuest(participant.guest_id, e)}
                                                  sx={{ cursor: 'pointer' }}
                                                >
                                                  View Guest
                                                </Link>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          ))
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {tours.length} tour{tours.length !== 1 ? 's' : ''} for {months[month - 1].label} {year}
              </Typography>
            </Box>
          </>
        )}

        <Dialog open={excludeDialogOpen} onClose={() => !excludeSaving && setExcludeDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Exclude tour from metrics & review SMS?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This instance will be omitted from dashboard and report totals. Pending review SMS for this start time
              will be cancelled.
            </Typography>
            <TextField
              label="Reason (optional)"
              value={excludeReason}
              onChange={(e) => setExcludeReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g. Cancelled in Bookeo after manifest"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExcludeDialogOpen(false)} disabled={excludeSaving}>
              Cancel
            </Button>
            <Button onClick={handleConfirmExclude} variant="contained" color="warning" disabled={excludeSaving}>
              {excludeSaving ? 'Saving…' : 'Confirm exclusion'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Tours;

